import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ApiSuccessResponse } from "@erp/shared";
import { apiClient } from "./client";

export type LegalCaseStatus = "ACTIVO" | "SUSPENDIDO" | "ARCHIVADO" | "TERMINADO";

export const LEGAL_CASE_STATUS_LABEL: Record<LegalCaseStatus, string> = {
  ACTIVO: "Activo",
  SUSPENDIDO: "Suspendido",
  ARCHIVADO: "Archivado",
  TERMINADO: "Terminado",
};

export interface LegalCaseRecord {
  id: string;
  clientId: string;
  title: string;
  caseType: string | null;
  caseNumber: string | null;
  court: string | null;
  opposingParty: string | null;
  description: string | null;
  status: LegalCaseStatus;
  filingDate: string | null;
  nextHearingDate: string | null;
  closedDate: string | null;
  claimAmount: string | null;
  notes: string | null;
  createdAt: string;
  client?: { id: string; fullName: string | null; businessName: string | null; tradeName: string | null; clientType: string } | null;
  assignedTo?: { id: string; fullName: string; email: string } | null;
}

export interface ListLegalCasesFilters {
  page?: number;
  pageSize?: number;
  clientId?: string;
  status?: LegalCaseStatus;
  caseType?: string;
  search?: string;
  upcomingHearing?: boolean;
}

export function useLegalCases(filters: ListLegalCasesFilters = {}, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: ["legal-cases", filters],
    enabled: options.enabled,
    queryFn: async () => {
      const { data } = await apiClient.get<ApiSuccessResponse<LegalCaseRecord[]>>("/legal-cases", { params: filters });
      return data;
    },
  });
}

export interface CreateLegalCasePayload {
  clientId: string;
  title: string;
  caseType?: string;
  caseNumber?: string;
  court?: string;
  opposingParty?: string;
  description?: string;
  status?: LegalCaseStatus;
  filingDate?: string;
  nextHearingDate?: string;
  closedDate?: string;
  claimAmount?: number | string;
  notes?: string;
  assignedToUserId?: string;
}

export function useCreateLegalCase() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreateLegalCasePayload) => {
      const { data } = await apiClient.post<ApiSuccessResponse<LegalCaseRecord>>("/legal-cases", payload);
      return data.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["legal-cases"] }),
  });
}

export function useUpdateLegalCase() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...payload }: Partial<CreateLegalCasePayload> & { id: string }) => {
      const { data } = await apiClient.patch<ApiSuccessResponse<LegalCaseRecord>>(`/legal-cases/${id}`, payload);
      return data.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["legal-cases"] }),
  });
}

export function useDeleteLegalCase() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/legal-cases/${id}`);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["legal-cases"] }),
  });
}

export function legalCaseClientName(legalCase: LegalCaseRecord): string | null {
  if (!legalCase.client) return null;
  return legalCase.client.clientType === "PERSONA_JURIDICA"
    ? legalCase.client.businessName || legalCase.client.tradeName || "Cliente"
    : legalCase.client.fullName || "Cliente";
}

export function hasUpcomingHearing(legalCase: LegalCaseRecord): boolean {
  if (!legalCase.nextHearingDate) return false;
  if (legalCase.status === "ARCHIVADO" || legalCase.status === "TERMINADO") return false;
  return new Date(legalCase.nextHearingDate) >= new Date(new Date().toDateString());
}
